<template>
  <div class="flex flex-col justify-start items-center">
    <!-- free transcriptor -->
    <div
      class="flex flex-col items-center justify-start w-full lg:w-[90%] min-h-[450px]"
    >
      <p class="text-lg md:text-3xl lg:text-4xl pb-4 text-[#061B44] text-center font-bold">
        {{ $t("transcribe-youtube-video") }}
      </p>
      <!-- send video link -->
      <div
        v-if="!isLoading && !hasCaption"
        class="relative white flex flex-col justify-center h-auto items-start w-full md:w-[90%] lg:w-[50%] xl:w-[40%] shadow-2xl rounded-xl p-4"
      >
        <p class="text-slate-500 font-medium text-sm pt-1">
          {{
            $t("enter-the-youtube-video-link-and-select-the-source-language")
          }}
        </p>
        <div></div>
        <input
          type="text"
          v-model="userInput"
          @input="fetchVideoLanguage"
          placeholder="https://www.youtube.com/watch?v="
          @keydown.ctrl.x.exact="handleCtrlX"
          @keydown.enter.exact="transcriptVideo"
          :class="{ 'pr-4': locale === 'ar' }"
          class="text-field w-full mt-4 lg:mt-1 pb-4 h-10 text-start pl-4 pt-4 lg:pt-3 outline-none font-medium bg-white border border-slate-700 rounded-lg px-2"
          required
        />
        <p class="text-slate-900 font-semibold text-sm pt-2 pb-1">
          {{ $t("source-language") }}
        </p>
        <button
          type="button"
          @click="toggleOriginalLanguageList"
          :disabled="isFetchingLanguage"
          :class="{
            'space-x-reverse': locale === 'ar',
            'opacity-50 cursor-not-allowed': isFetchingLanguage,
          }"
          class="w-full flex justify-center items-center space-x-4 bg-white h-10 p-3 rounded-lg border border-slate-900"
        >
          <template v-if="!isFetchingLanguage">
            <span
              class="inline-flex items-center gap-1.5"
              :class="{ 'flex-row-reverse': locale === 'ar' }"
            >
              <i
                class="pi pi-globe text-slate-700 opacity-80 flex-shrink-0 text-sm md:text-base"
                aria-hidden="true"
              ></i>
              <span class="text-slate-900 font-medium text-sm md:text-base lg:text-lg">
                {{ originalLanguage.langName }}
              </span>
            </span>
          </template>
          <template v-else>
            <p class="text-slate-900 font-medium text-sm md:text-base lg:text-lg">
              {{ $t("detecting-language") }}
            </p>
            <img
              class="w-5 h-5 animate-spin"
              src="../assets/icons/loading-loader.svg"
              alt=""
            />
          </template>
          <img
            v-if="!isFetchingLanguage"
            src="../assets/images/audio/02.svg"
            alt=""
            class="w-[5%] lg:w-[3%] text-white fill-white"
          />
        </button>
        <div
          v-if="
            authorLangSelected &&
            originalLanguage.langCode !== authorLanguage.langCode
          "
          class="flex flex-row space-x-2 p-4 border-[1px] border-orange-300 bg-orange-50 rounded-md mt-3 text-sm items-center justify-center text-orange-600 w-full"
        >
          <i class="pi pi-info-circle"></i>
          <p>
            {{ $t("the-selected-language-differs-from-the") }}
            ( {{ authorLanguage.langName }} ).
            {{ $t("transcription-quality-may-vary") }}
          </p>
        </div>
        <button
          type="button"
          @click="transcriptVideo"
          :disabled="isFetchingLanguage"
          :class="{
            'opacity-50 cursor-not-allowed': isFetchingLanguage,
          }"
          class="button px-20 py-1 mt-4 bg-subDark text-white text-sm md:text-base lg:text-lg font-bold w-full rounded-lg h-10"
        >
          {{ $t("Transcript_Video") }}
        </button>
        <!-- original language gridview -->
        <div v-if="isOpen" class="overlay z-50" @click="closeList">
          <div
            class="overlay-content flex flex-col items-center min-w-[80%] h-[70%]"
            @click.stop
          >
            <div
              :class="{ 'space-x-reverse': locale === 'ar' }"
              class="flex flex-row space-x-4 w-full items-center justify-center h-10 py-4"
            >
              <input
                v-model="searchQuery"
                @input="filteredLanguages"
                type="text"
                :placeholder="$t('search-for-a-language')"
                class="pl-4 bg-white border border-slate-900 rounded-md text-slate-900 text-base font-semibold w-[40%] h-10"
              />
              <button
                @click="closeList"
                class="bg-white border border-slate-900 rounded-md text-slate-900 text-lg font-bold py-1 px-8 h-10"
              >
                {{ $t("close") }}
              </button>
            </div>
            <ul
              class="py-1 h-75vh grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4 overflow-y-auto"
            >
              <li
                v-for="lang in myLanguagesList"
                :key="lang.langName"
                class="flex flex-row space-x-2"
              >
                <a
                  @click="selectLanguage(lang)"
                  class="flex items-center gap-2 px-4 py-2 text-gray-800 font-semibold cursor-pointer"
                >
                  <i
                    class="pi pi-globe text-gray-500 flex-shrink-0"
                    style="font-size: 0.875rem"
                    aria-hidden="true"
                  ></i>
                  {{ lang.langName }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <!-- operations card -->
      <div
        v-if="isLoading"
        class="relative white flex flex-col justify-center h-auto items-center w-[90%] lg:w-[50%] xl:w-[40%] shadow-2xl rounded-xl p-4"
      >
        <img
          class="w-12 h-12 lg:w-20 lg:h-20 animate-spin fill-subDark"
          src="../assets/icons/loading-loader.svg"
          alt="Loading icon"
        />
        <p class="text-[#061B44] font-semibold text-xl">
          {{ $t("retrieving-captions") }}
        </p>
        <p class="text-slate-500 font-medium text-sm">
          {{ $t("this-may-take-a-minute-hang-tight") }}
        </p>
      </div>
      <div
        v-if="hasCaption"
        class="rounded-2xl shadow-2xl p-6 w-[90%] lg:w-[75%] xl:w-[65%] items-center justify-center min-h-96"
      >
        <!-- text containers -->
        <div class="container pt-4 h-auto">
          <!-- original text container -->
          <p class="text-[#061B44] font-semibold text-xl pb-2">
            {{ $t("source-text") }}
          </p>
          <div
            class="container bg-white rounded-xl w-full h-auto p-2 flex flex-col items-center justify-center border"
          >
            <textarea
              v-model="originalText"
              rows="10"
              class="block md:p-2.5 w-full h-72 text-sm md:text-lg font-medium md:font-semibold outline-none text-slate-900 bg-white rounded-lg shadow-sm"
            ></textarea>
            <div
              :class="{ 'md:space-x-reverse': locale === 'ar' }"
              class="container flex flex-col md:flex-row items-center space-y-2 md:space-y-0 justify-start py-2 md:space-x-2"
            >
              <button
                @click="toggleDownloadTypes"
                class="w-full md:w-[55%] flex justify-center items-center md:space-x-4 bg-white border border-slate-900 h-10 p-2 md:p-3 rounded-lg"
              >
                <img
                  src="../assets/images/audio/04.png"
                  alt=""
                  class="h-3 md:h-5"
                />
                <p class="text-[#061B44] font-semibold text-sm md:text-base">
                  {{ $t("download") }}
                </p></button
              ><button
                @click="copyText"
                class="w-full md:w-[55%] flex justify-center items-center md:space-x-4 bg-white border border-slate-900 h-10 p-2 md:p-3 rounded-lg"
              >
                <img
                  src="../assets/images/audio/05.png"
                  alt=""
                  class="h-3 md:h-5"
                />
                <p class="text-[#061B44] font-semibold text-sm md:text-base">
                  {{ $t("Copy") }}
                </p>
              </button>
              <div
                v-if="showDownloadTypes"
                class="overlay z-50"
                @click="toggleDownloadTypes"
              >
                <div class="overlay-content">
                  <ul
                    class="py-1 h-75vh grid grid-cols-2 lg:grid-cols-3 gap-4 overflow-y-auto"
                  >
                    <li
                      v-for="type in supportFileDownloadTypes"
                      :key="type"
                      class="flex flex-row space-x-2"
                    >
                      <a
                        @click="downloadFileAs(type)"
                        class="block px-4 py-2 text-gray-800 font-semibold cursor-pointer border border-slate-700 rounded-lg w-full text-center"
                      >
                        {{ type }}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <button
                @click="processNewVideo"
                class="w-full md:w-[55%] flex justify-center items-center md:space-x-4 bg-white border border-slate-900 h-10 p-2 md:p-3 rounded-lg"
              >
                <svg
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  stroke-linejoin="round"
                  class="h-6 w-6 md:h-6 text-[#061B44]"
                >
                  <path
                    d="m3.508 6.726c1.765-2.836 4.911-4.726 8.495-4.726 5.518 0 9.997 4.48 9.997 9.997 0 5.519-4.479 9.999-9.997 9.999-5.245 0-9.553-4.048-9.966-9.188-.024-.302.189-.811.749-.811.391 0 .715.3.747.69.351 4.369 4.012 7.809 8.47 7.809 4.69 0 8.497-3.808 8.497-8.499 0-4.689-3.807-8.497-8.497-8.497-3.037 0-5.704 1.597-7.206 3.995l1.991.005c.414 0 .75.336.75.75s-.336.75-.75.75h-4.033c-.414 0-.75-.336-.75-.75v-4.049c0-.414.336-.75.75-.75s.75.335.75.75z"
                    fill-rule="nonzero"
                  />
                </svg>
                <p
                  class="text-[#061B44] font-semibold text-sm md:text-base text-nowrap"
                >
                  {{ $t("Start_a_New_Transcription") }}
                </p>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import { toast } from "vue3-toastify";
import { CaptionModel } from "../utils/captionModel";
import { Document, Packer, Paragraph, TextRun } from "docx";
import { getDocxExportFontSizeHalfPoints } from "../utils/docxExport";
import { API_BASE_URL, GET_CAPTIONS, YTVIDEOLANGUAGE } from "../utils/appConst";
import { Language, languages } from "../utils/languageList";
import { useI18n } from "vue-i18n";

const getCpationUrl = `${API_BASE_URL}${GET_CAPTIONS}`;
const fetchYoutubeLanguageUrl = `${API_BASE_URL}${YTVIDEOLANGUAGE}`;
const { t, locale } = useI18n();
const isNotValidLink = ref(false);
const hasCaption = ref(false);
const isLoading = ref(false);
const isFetchingLanguage = ref(false);
const isOpen = ref(false);
const showDownloadTypes = ref(false);
const premiumService = ref(false);
const userInput = ref("");
const originalText = ref("");
const originalVideoSubtitle = ref("");
const myLanguagesList = ref<Language[]>(languages);
const searchQuery = ref("");
const authorLangSelected = ref<boolean>(false);
const originalLanguage = ref<Language>({
  langName: "English",
  langCode: "en",
  countryCode: "us",
});
const authorLanguage = ref<Language>({
  langName: "English",
  langCode: "en",
  countryCode: "us",
});

const toggleDownloadTypes = () => {
  showDownloadTypes.value = !showDownloadTypes.value;
};

const closeList = () => {
  isOpen.value = false;
  resetFilter();
};

const selectLanguage = (lang: Language) => {
  originalLanguage.value = { ...lang };
  isOpen.value = false;
};

const toggleOriginalLanguageList = () => {
  if (isFetchingLanguage.value) return;
  isOpen.value = !isOpen.value;
};

const handleCtrlX = (event: any) => {
  if (event.key === "x" && event.ctrlKey) {
    event.preventDefault();
    const inputElement = event.target;
    inputElement.select();
    document.execCommand("cut");
  }
};

const filteredLanguages = () => {
  if (searchQuery.value.length == 0) {
    myLanguagesList.value = languages;
  } else {
    const lowerCaseQuery = searchQuery.value.toLowerCase();
    myLanguagesList.value = languages.filter((langs) =>
      langs.langName.toLowerCase().includes(lowerCaseQuery)
    );
  }
};

const resetFilter = () => {
  searchQuery.value = "";
  myLanguagesList.value = languages;
};

function isFreeYoutubeNoCaptionsError(result: {
  code?: string;
  error?: unknown;
}): boolean {
  if (result?.code === "NO_YOUTUBE_CAPTIONS") return true;
  const err = result?.error;
  if (typeof err !== "string") return false;
  return (
    err === "No captions found for the specified video" ||
    err === "No captions found" ||
    err.startsWith("No captions found ")
  );
}

const fetchVideoLanguage = async () => {
  try {
    if (isValidYouTubeUrl(userInput.value)) {
      const videoId = extractVideoId(userInput.value);
      if (videoId) {
        isFetchingLanguage.value = true;
        isOpen.value = false;
        if (authorLangSelected.value) {
          authorLangSelected.value = false;
        }
        try {
          const getData = await fetch(fetchYoutubeLanguageUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "ngrok-skip-browser-warning": "69420",
            },
            body: JSON.stringify({
              videoId: videoId,
            }),
          });
          if (!getData.ok) {
            throw new Error(`HTTP error! Status: ${getData.status}`);
          }
          const data = await getData.json();
          if (data.language && data.language != "Not specified") {
            const lng = data.language.includes("-")
              ? data.language.split("-")[0]
              : data.language;
            let foundLanguage = languages.find(
              (language) => language.langCode === lng
            );
            if (!foundLanguage) {
              foundLanguage = languages.find(
                (language) =>
                  language.langCode.startsWith(lng + "-") ||
                  language.langCode === lng
              );
            }
            if (foundLanguage) {
              originalLanguage.value.langName = foundLanguage.langName;
              originalLanguage.value.langCode = foundLanguage.langCode;
              originalLanguage.value.countryCode = foundLanguage.countryCode;
              authorLanguage.value.langName = foundLanguage.langName;
              authorLanguage.value.langCode = foundLanguage.langCode;
              authorLanguage.value.countryCode = foundLanguage.countryCode;
              authorLangSelected.value = true;
            } else {
              originalLanguage.value.langName = "English";
              originalLanguage.value.langCode = "en";
              originalLanguage.value.countryCode = "us";
            }
          } else {
            originalLanguage.value.langName = "English";
            originalLanguage.value.langCode = "en";
            originalLanguage.value.countryCode = "us";
          }
        } catch (error) {
          console.log(`fetchVideoLanguage: ${error}`);
          originalLanguage.value.langName = "English";
          originalLanguage.value.langCode = "en";
          originalLanguage.value.countryCode = "us";
        } finally {
          isFetchingLanguage.value = false;
        }
      } else {
        isFetchingLanguage.value = false;
      }
    } else {
      isFetchingLanguage.value = false;
    }
  } catch (error) {
    console.log(`fetchVideoLanguage: ${error}`);
    isFetchingLanguage.value = false;
  }
};

const transcriptVideo = async () => {
  try {
    if (isFetchingLanguage.value) {
      return;
    }
    if (userInput.value.trim().length == 0) {
      return;
    }
    isNotValidLink.value = false;
    hasCaption.value = false;
    premiumService.value = false;
    if (isValidYouTubeUrl(userInput.value)) {
      isLoading.value = true;
      const videoId = extractVideoId(userInput.value);
      isNotValidLink.value = false;
      const response = await fetch(getCpationUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "69420",
        },
        body: JSON.stringify({
          videoLink: userInput.value,
          videoOriginLang: originalLanguage.value.langCode,
        }),
      });

      const result = await response.json();

      if (result.error) {
        if (result.error == "This is not a valid YouTube link.") {
          isNotValidLink.value = true;
          userInput.value = "";
          isLoading.value = false;
          toast(
            t(
              "oops-looks-like-the-youtube-link-you-entered-is-invalid-please-double-check-and-try-again-thanks"
            ),
            {
              autoClose: 2500,
              type: "error",
              theme: "light",
            }
          );
          return;
        } else if (isFreeYoutubeNoCaptionsError(result)) {
          premiumService.value = true;
          toast(t("Oops_This_YouTube_video_doesnt_have_captions"), {
            autoClose: 3500,
            type: "error",
            theme: "light",
          });
          userInput.value = "";
          isLoading.value = false;
          return;
        } else {
          isLoading.value = false;
          toast(
            t('were-fixing-a-technical-issue-so-please-try'),
            {
              autoClose: 2500,
              type: "error",
              theme: "light",
            }
          );
        }
      } else if (!result.error) {
        originalText.value = extractText(result.captions);
        originalVideoSubtitle.value = subtitleGenerationFromCaption(
          result.captions
        );
        hasCaption.value = true;
        isLoading.value = false;
      }
    } else {
      isNotValidLink.value = true;
      userInput.value = "";
      toast(
        t(
          "oops-looks-like-the-youtube-link-you-entered-is-invalid-please-double-check-and-try-again-thanks"
        ),
        {
          autoClose: 2500,
          type: "error",
          theme: "light",
        }
      );
    }
  } catch (error) {
    console.error("Error making request to the server:", error);
    isLoading.value = false;
    toast(
      t('were-fixing-a-technical-issue-so-please-try'),
      {
        autoClose: 2500,
        type: "error",
        theme: "light",
      }
    );
  }
};

const isValidYouTubeUrl = (url: string): boolean => {
  const youtubeRegex = /^(https?\:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+/;
  return youtubeRegex.test(url);
};

const extractVideoId = (url: string): string | null => {
  const videoIdMatch = url.match(
    /(?:youtube\.com\/(?:shorts\/|(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/))([a-zA-Z0-9_-]{11})/
  );
  return videoIdMatch ? videoIdMatch[1] : null;
};

const processNewVideo = () => {
  try {
    hasCaption.value = false;
    isLoading.value = false;
    isNotValidLink.value = false;
  } catch (error) {
    console.log(error);
  }
};

function subtitleGenerationFromCaption(captionsResponse: any): string {
  try {
    const captions: CaptionModel[] = [];
    captionsResponse.forEach((result: any) => {
      const text = result.text.trim();
      if (text.length === 0) {
        return;
      }
      const newCaption: CaptionModel = {
        start: formatTime(parseFloat(result.start)),
        dur: formatTime(parseFloat(result.start) + parseFloat(result.dur)),
        text: text,
      };
      captions.push(newCaption);
    });

    let webvttContent = "WEBVTT\n\n";
    captions.forEach((caption, index) => {
      webvttContent += `${index + 1}\n${caption.start} --> ${caption.dur}\n${
        caption.text
      }\n\n`;
    });
    return webvttContent;
  } catch (error) {
    console.error("Error processing captions:", error);
    return "";
  }
}

function formatTime(seconds: number): string {
  const date = new Date(seconds * 1000);
  const hours = date.getUTCHours().toString().padStart(2, "0");
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  const secondsFormatted = date.getSeconds().toString().padStart(2, "0");
  const milliseconds = (date.getMilliseconds() / 1000).toFixed(3).slice(2, 5);
  return `${hours}:${minutes}:${secondsFormatted}.${milliseconds}`;
}

const extractText = (data: any) => {
  try {
    let concatenatedTranscript = "";
    data.forEach((caption: any) => {
      let text = caption["text"]!.trim();
      concatenatedTranscript += text + " ";
    });
    return concatenatedTranscript.trim();
  } catch (error) {
    console.log(error);

    return "";
  }
};

const downloadFileAs = async (type: string) => {
  try {
    if (type === "txt") {
      downloadAsTxt(originalText.value);
    } else if (type === "pdf") {
      downloadAsPdf(originalText.value);
    } else if (type === "doc") {
      await downloadAsDoc(originalText.value);
    } else if (type === "webvtt") {
      downloadWebVttFile();
    } else if (type === "srt") {
      downloadSrtFile();
    } else {
      console.log("Unsupported file type");
    }
  } catch (error) {
    console.log("doenload error: ", error);
  }
};

function webVttToSrt(webVttContent: any) {
  const lines = webVttContent.split("\n");
  let srtContent = "";
  let counter = 1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes("-->")) {
      srtContent += counter + "\n";
      counter++;
      const timecode = lines[i].replace(".", ",").replace(" --> ", " --> ");
      srtContent += timecode + "\n";
      i++;
      while (i < lines.length && lines[i].trim() !== "") {
        srtContent += lines[i] + "\n";
        i++;
      }
      srtContent += "\n";
    }
  }
  return srtContent;
}

const downloadWebVttFile = () => {
  if (originalVideoSubtitle.value.length == 0) {
    return;
  }
  const blob = new Blob([originalVideoSubtitle.value], { type: "text/vtt" });
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = `${generateFileName()}.vtt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const downloadSrtFile = () => {
  if (originalVideoSubtitle.value.length == 0) {
    return;
  }
  const srtContent = webVttToSrt(originalVideoSubtitle.value);
  const blob = new Blob([srtContent], { type: "text/srt" });
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = `${generateFileName()}.srt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const downloadAsTxt = (text: string) => {
  const blob = new Blob([text], {
    type: "text/plain;charset=utf-8",
  });
  saveAs(blob, `${generateFileName()}.txt`);
};

const normalizeText = (text: string) => {
  return text
    .split("\n")
    .reduce((acc: any, line) => {
      const trimmedLine = line.trim();
      if (trimmedLine) {
        if (acc.length === 0) {
          return [trimmedLine];
        } else {
          const lastLine = acc[acc.length - 1];
          if (
            lastLine.endsWith(".") ||
            lastLine.endsWith("?") ||
            lastLine.endsWith("!")
          ) {
            return [...acc, trimmedLine];
          } else {
            return [...acc.slice(0, -1), `${lastLine} ${trimmedLine}`];
          }
        }
      }
      return acc;
    }, [])
    .join("\n");
};

const downloadAsPdf = async (val: string) => {
  try {
    const isArabic: boolean = originalLanguage.value.langCode === "ar";
    const text = normalizeText(val);
    const doc = new jsPDF("p", "pt", "a4");
    const margin = 56.7;
    if (isArabic) {
      doc.setLanguage("ar");
    }
    doc.setFont("helvetica");
    doc.setFontSize(12);
    doc.setLineHeightFactor(1.5);
    const pageWidth = doc.internal.pageSize.width - 20;
    const pageHeight = doc.internal.pageSize.height;
    const maxLineWidth = pageWidth - margin * 2;
    const lines = doc.splitTextToSize(text, maxLineWidth);

    let currentHeight = margin;

    lines.forEach((line: string | string[]) => {
      if (currentHeight + doc.getLineHeight() > pageHeight - margin) {
        doc.addPage();
        currentHeight = margin;
      }
      if (isArabic) {
        doc.text(line, pageWidth - margin, currentHeight, { align: "right" });
      } else {
        doc.text(line, margin, currentHeight);
      }
      currentHeight += doc.getLineHeight();
    });
    doc.save(`${generateFileName()}.pdf`);
  } catch (error) {
    console.log("downloadAsPdf error: ", error);
  }
};
const downloadAsDoc = async (text: string) => {
  const docxFontSizeHalfPoints = getDocxExportFontSizeHalfPoints();
  const doc = new Document({
    sections: [
      {
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text,
                size: docxFontSizeHalfPoints,
              }),
            ],
          }),
        ],
      },
    ],
  });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${generateFileName()}.docx`);
};

const copyText = () => {
  navigator.clipboard
    .writeText(originalText.value)
    .then(() => {
      toast(t("text-copied-to-clipboard"), {
        autoClose: 1500,
        type: "success",
        theme: "light",
      });
    })
    .catch((error) => {
      toast(t("failed-to-copy-text-please-try-again"), {
        autoClose: 1500,
        type: "error",
        theme: "light",
      });
      console.error("Error copying text:", error);
    });
};

const removeParentheses = (langName: string) => {
  return langName.replace(/\s*\(.*?\)\s*/g, "").trim();
};

function generateFileName(): string {
  let fileName: string = `Subtitles.tech-youtube_${extractVideoId(
    userInput.value
  )}_${originalLanguage.value.langName}`;
  return fileName;
}

const supportFileDownloadTypes = ["txt", "webvtt", "srt", "doc"];
</script>
